import { useCallback, useEffect, useRef, useState } from 'react'

import cn from '../Forms.module.scss'

type SMSCodeInputProps = {
  onComplete: (code: string) => void
  isSuccess?: boolean
}

export function SMSCodeInput({ onComplete, isSuccess }: SMSCodeInputProps) {
  const [codeDigits, setCodeDigits] = useState(['', '', '', ''])
  const codeInputsRef = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    const fullCode = codeDigits.join('')
    if (fullCode.length === 4) {
      onComplete(fullCode)
    }
  }, [codeDigits, onComplete])

  const handleCodeChange = useCallback(
    (index: number, value: string) => {
      if (!/^\d?$/.test(value)) return

      const newDigits = [...codeDigits]
      newDigits[index] = value

      if (value && index < 3) {
        codeInputsRef.current[index + 1]?.focus()
      }

      setCodeDigits(newDigits)
    },
    [codeDigits]
  )

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      setCodeDigits((prev) => {
        if (!prev[index] && index > 0) {
          const newDigits = [...prev]
          newDigits[index - 1] = ''
          setTimeout(() => codeInputsRef.current[index - 1]?.focus(), 0)
          return newDigits
        }
        return prev
      })
    }
    if (e.key === 'ArrowLeft' && index > 0)
      codeInputsRef.current[index - 1]?.focus()
    if (e.key === 'ArrowRight' && index < 3)
      codeInputsRef.current[index + 1]?.focus()
  }, [])

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault()
      const pasteData = e.clipboardData
        .getData('Text')
        .replace(/\D/g, '')
        .slice(0, 4)
      if (pasteData.length === 4) {
        const digits = pasteData.split('')
        setCodeDigits(digits)
        onComplete(pasteData)
      }
    },
    [onComplete]
  )

  useEffect(() => {
    codeInputsRef.current[0]?.focus()
  }, [])

  return (
    <div className={cn.codeInputsContainer}>
      {codeDigits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (codeInputsRef.current[index] = el)}
          className={[
            cn.codeInput,
            isSuccess && cn.successAnimating,
            digit && cn.filled,
          ]
            .filter(Boolean)
            .join(' ')}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleCodeChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={(e) => e.target.select()}
          onPaste={handlePaste}
          style={{ '--code-input-delay': `${index * 100}ms` }}
        />
      ))}
    </div>
  )
}
